// `avatar sync [--version <tag>] [--dry-run] [--force]` — v3.
//
// Tải team-ai-pack tarball mới từ Supabase (thay git submodule update) + tạo
// symlink farm + merge settings + re-apply tools.
//
// Flow:
//   1. Locate project root (cwd có .claude/pack/)
//   2. getValidIdToken → fetchSignedPackUrl(version?) → download+extract → manifest
//      (--version pin bản cụ thể; không flag → server trả stable mới nhất)
//   3. --dry-run? show preview (version diff) và exit
//   4. syncAllMountDirs() → symlink
//   5. merge settings + re-apply tools

import { readFile } from "node:fs/promises";
import { basename, dirname, join, resolve } from "node:path";
import type { Command } from "commander";

import { assertInAvatarWorkspaceOrExit } from "../lib/assert-in-avatar-workspace-or-hint-foreign.js";
import { classifyPackVersionTransition } from "../lib/compare-pack-semver-and-detect-downgrade.js";
import { downloadTeamPackAndWriteManifest } from "../lib/download-team-pack-and-write-manifest.js";
import { ensureClaudeGitignoreHasRequiredPatterns } from "../lib/ensure-claude-gitignore-has-required-patterns.js";
import { ensureGitnexusInstalledAndLatest } from "../lib/ensure-gitnexus-installed-and-latest.js";
import {
  appendOrientationImportToClaudeMd,
  ensureOrientationNoteFromPack,
  type OrientationStatus,
} from "../lib/ensure-orientation-note-from-pack.js";
import { ensureSecurityScannersInstalled } from "../lib/ensure-security-scanners-installed.js";
import { ensureValidTokenOrLogin } from "../lib/ensure-valid-token-or-login.js";
import { installPackFromLocalDir } from "../lib/install-pack-from-local-dir.js";
import {
  writeMachineReadableError,
  writeMachineReadableJson,
} from "../lib/machine-readable-json-output-contract.js";
import { mergePackSettingsIntoProjectSettings } from "../lib/merge-pack-settings-into-project-settings.js";
import { readPackVersionFromFile, writePackManifest } from "../lib/pack-manifest-store.js";
import {
  type PatchExcludeResult,
  patchAllReposGitExclude,
} from "../lib/patch-all-repos-git-exclude.js";
import { DEFAULT_STALE_DAYS } from "../lib/plan-sync-all-across-registered-workspaces.js";
import {
  buildSyncPreview,
  type SyncPreview,
} from "../lib/preview-team-pack-sync-changes-for-dry-run.js";
import { mergeRootClaudeMdManaged } from "../lib/project-tree-scaffolder.js";
import { pruneDeadAvatarModeEnvPlaceholder } from "../lib/prune-dead-avatar-mode-env-placeholder.js";
import { pruneOrphanToolHooks } from "../lib/prune-orphan-tool-hooks.js";
import { pruneStaleAvatarPrePushHooks } from "../lib/prune-stale-avatar-pre-push-hooks.js";
import { readCliVersion } from "../lib/read-cli-version-from-package-json.js";
import { readLocalPackVersion } from "../lib/read-local-pack-version.js";
import { reapplyEnabledToolsAfterSync } from "../lib/reapply-enabled-tools-after-sync.js";
import { stripAvatarBlockFromReposGitignore } from "../lib/strip-avatar-block-from-repo-gitignores.js";
import { InvalidIdTokenError } from "../lib/supabase-pack-downloader.js";
import {
  type SyncMountedDirResult,
  syncAllMountDirs,
} from "../lib/symlink-farm-for-team-pack-mount-dirs.js";
import { TEAM_PACK_RELATIVE_PATH } from "../lib/team-pack-manager.js";
import { log, setMachineReadableMode } from "../lib/terminal-logger.js";
import { readToolState, setToolState } from "../lib/tool-state-store.js";
import { recordWorkspaceInRegistry } from "../lib/workspace-registry-store.js";
import {
  runSyncAllAcrossRegisteredWorkspaces,
  type SyncAllOptions,
} from "./sync-all-across-registered-workspaces.js";

interface SyncOptions {
  force?: boolean;
  packVersion?: string;
  dryRun?: boolean;
  local?: string;
  json?: boolean;
}

// Cảnh báo khi cài đè pack bằng bản CŨ HƠN bản đang có (ticket #191).
// `avatar sync` trần luôn kéo stable mới nhất, nên máy đang chạy bản beta mới hơn
// sẽ bị hạ cấp mà không có dấu hiệu gì. Chỉ IN THÊM 1 dòng — không hỏi, không chặn,
// không đổi exit code.
//
// Fail-soft tuyệt đối: hàm nằm trong `try` có `process.exit(1)`, nên lỗi đọc manifest
// KHÔNG được phép giết luồng sync. Version không parse được ⇒ im lặng (unknown).
async function warnIfPackDowngrade(
  packDir: string,
  targetVersion: string,
  keepHint: string,
): Promise<void> {
  try {
    const current = await readPackVersionFromFile(packDir);
    if (classifyPackVersionTransition(current, targetVersion) !== "downgrade") return;
    // keepHint là PREFIX; version cũ nối ở đuôi vì `current` chỉ có bên trong hàm này.
    log.warn(
      `Hạ cấp pack: ${current} → ${targetVersion} — bản đang cài MỚI HƠN bản sắp cài. ` +
        `Giữ bản cũ: ${keepHint} ${current}`,
    );
  } catch {
    // fail-soft: cảnh báo là phụ, không bao giờ chặn sync
  }
}

// Lấy metadata để render lại managed block. Vì marker bọc TOÀN BỘ CLAUDE.md
// (kể cả # tên + mô tả), sync không parse từ file cũ (sẽ kéo nội dung cũ rác
// quay lại). Thay vào đó:
//   - projectName  = tên thư mục workspace (ổn định, là danh tính thật).
//   - teamOwner    = đọc best-effort từ CLAUDE.md cũ TRONG marker (chỉ field
//                    metadata thuần, không phải nội dung tự do) — giữ giá trị
//                    init đã set; không có thì rỗng.
//   - description  = rỗng (init set; sync không tái tạo mô tả tự do).
async function readClaudeMdMeta(
  projectRoot: string,
  claudeMdPath: string,
): Promise<{ projectName: string; projectDescription: string; teamOwner: string }> {
  const projectName = basename(projectRoot) || "workspace";
  let owner = "";
  try {
    const text = await readFile(claudeMdPath, "utf8");
    owner = text.match(/^-\s*Team owner:\s*(.+)$/m)?.[1]?.trim() ?? "";
  } catch {
    // ignore — owner rỗng
  }
  return { projectName, projectDescription: "", teamOwner: owner };
}

/**
 * Dựng payload `avatar sync --dry-run --json`. Hàm THUẦN (không I/O) — `buildSyncPreview`
 * đã làm hết phần đo đĩa, đây chỉ đổi tên field sang snake_case của hợp đồng.
 *
 * `status` giữ NGUYÊN chuỗi của `inspectMountDir` (`already-linked` · `needs-creation` ·
 * `conflict-real-dir` · `source-missing`). `conflict-real-dir` chính là tín hiệu
 * "symlink farm hỏng" mà extension cần để hiện nút `avatar sync --force` — đừng gộp
 * nó chung với các trạng thái khác.
 */
export function buildSyncPreviewJsonPayload(preview: SyncPreview): {
  current_version: string;
  target_version: string;
  mount_dirs: Array<{ dir: string; status: string }>;
} {
  return {
    current_version: preview.currentVersion,
    target_version: preview.targetVersion,
    mount_dirs: preview.mountDirStatuses.map((m) => ({ dir: m.dir, status: m.status })),
  };
}

async function syncAction(opts: SyncOptions): Promise<void> {
  // #280: luật 4 — stdout chỉ chứa JSON. Bật ngay đầu action: guard workspace và
  // buildSyncPreview đều có thể in log người-đọc trước khi tới payload.
  if (opts.json) setMachineReadableMode(true);
  // Guard: cwd phải là Avatar workspace (marker .claude/avatar.json). Đứng nhầm
  // workspace avatar-po → báo dùng 'avatar-po'. Thay check pathExists(.claude/) cũ
  // (quá lỏng: mọi thư mục có .claude/ đều qua, kể cả workspace của CLI kia).
  const projectRoot = assertInAvatarWorkspaceOrExit(process.cwd());
  const claudeDir = join(projectRoot, ".claude");
  const packDir = join(projectRoot, TEAM_PACK_RELATIVE_PATH);

  // --json chỉ có nghĩa cùng --dry-run: đường apply thật in tiến trình/spinner ra
  // stdout, không thể vừa chạy vừa giữ stdout sạch cho JSON (luật 4 của hợp đồng).
  // Báo bằng chính JSON để phía gọi (extension) đọc được, đừng bắt nó parse text.
  if (opts.json && !opts.dryRun) {
    writeMachineReadableError("sync", "--json chỉ dùng cùng --dry-run");
    process.exit(1);
  }

  // --dry-run: chỉ preview, không tải.
  if (opts.dryRun && !opts.local) {
    const preview = await buildSyncPreview(packDir, claudeDir, opts.packVersion);
    if (opts.json) {
      writeMachineReadableJson(buildSyncPreviewJsonPayload(preview));
      return;
    }
    log.info(`Pack version hiện tại: ${preview.currentVersion}`);
    log.info(`Target version:        ${preview.targetVersion}`);
    log.info("\nMount dir statuses:");
    for (const m of preview.mountDirStatuses) {
      log.plain(`  ${m.dir.padEnd(12)} ${m.status}`);
    }
    log.info("\nDry-run done. Không apply. Bỏ --dry-run để thực thi.");
    return;
  }

  let installedVersion: string;

  // --local: hermetic install from a local pack source. No token, no Supabase,
  // no endpoint allowlist. Reuses the same symlink farm + settings merge below.
  if (opts.local) {
    if (opts.packVersion) {
      log.error("--local không dùng chung với --pack-version (version chỉ áp dụng cho Supabase).");
      process.exit(1);
    }
    const sourceDir = resolve(opts.local);
    if (opts.dryRun) {
      log.info(`[dry-run] Sẽ cài pack từ local: ${sourceDir} → ${packDir}`);
      log.info("[dry-run] Sau đó dựng lại symlink farm + merge settings (không cần mạng).");
      return;
    }
    log.info(`Cài pack từ local source: ${sourceDir}`);
    try {
      // Đọc version ĐÍCH trước khi ghi đè, để còn so được với bản đang cài (#191).
      const targetVersion = await readLocalPackVersion(sourceDir);
      await warnIfPackDowngrade(packDir, targetVersion, "cài lại từ thư mục nguồn chứa");
      await installPackFromLocalDir(sourceDir, packDir);
      installedVersion = targetVersion;
      await writePackManifest(packDir, {
        version: installedVersion,
        downloadedObject: `local:${sourceDir}`,
        extractedAt: new Date().toISOString(),
      });
      log.success(`Đã cài pack local ${installedVersion}`);
    } catch (err) {
      log.error(`Cài pack local thất bại: ${err instanceof Error ? err.message : err}`);
      process.exit(1);
    }
  } else {
    // Tải tarball mới. Token chết (chưa login / refresh_token revoked) → force
    // login lại thay vì exit ngay, để `avatar sync` tự phục hồi được (DX).
    let idToken: string;
    try {
      idToken = await ensureValidTokenOrLogin();
    } catch (err) {
      log.error(err instanceof Error ? err.message : String(err));
      process.exit(1);
    }

    log.info(
      opts.packVersion
        ? `Tải team-ai-pack ${opts.packVersion} từ Supabase...`
        : "Tải team-ai-pack (stable mới nhất) từ Supabase...",
    );
    try {
      // Version ghi vào manifest suy từ `info.object` server trả về, KHÔNG phải
      // `opts.packVersion` thô — xem download-team-pack-and-write-manifest.ts (#227).
      const downloaded = await downloadTeamPackAndWriteManifest(
        packDir,
        idToken,
        opts.packVersion,
        (resolvedVersion) =>
          warnIfPackDowngrade(packDir, resolvedVersion, "avatar sync --pack-version"),
      );
      installedVersion = downloaded.installedVersion;
      log.success(`Đã cài pack ${installedVersion}`);
    } catch (err) {
      if (err instanceof InvalidIdTokenError) {
        log.error(`${err.message}`);
      } else {
        log.error(`Tải pack thất bại: ${err instanceof Error ? err.message : err}`);
      }
      process.exit(1);
    }
  }

  log.info("Tạo symlink farm...");
  const results = await syncAllMountDirs(packDir, claudeDir, opts.force === true);
  reportResults(results, opts.force === true);

  // Ticket #172: self-heal `.claude/.gitignore` cho workspace init TRƯỚC #164 (thiếu rào chắn
  // lock file). Merge — KHÔNG ghi đè: dòng user tự thêm phải còn nguyên.
  // ⚠️ VỊ TRÍ CÓ CHỦ ĐÍCH: phải đứng TRƯỚC cụm writer settings.json bên dưới, vì chính chúng sinh
  // ra bản backup + lock file của settings.json — rule cần có mặt trước. ĐỪNG dời xuống cụm
  // self-heal cuối luồng (hai bước đó ở cuối vì buộc chạy SAU mọi writer settings — ngược lại).
  // try/catch riêng, fail-soft; no-op → im lặng.
  // ⚠️ Nhánh `catch` dưới hôm nay là DEAD CODE có chủ đích: hàm heal tự bọc mọi lỗi thành
  // `status: "failed"` nên nó không reject. Giữ làm defense-in-depth cho ngày ai đó thêm đường
  // throw — ĐỪNG đọc sự tồn tại của nó như bằng chứng "hàm có thể throw".
  try {
    const gi = await ensureClaudeGitignoreHasRequiredPatterns(projectRoot);
    if (gi.status === "appended") {
      log.success(
        `  ✓ .claude/.gitignore: bổ sung ${gi.added.length} pattern (${gi.added.join(", ")})`,
      );
    } else if (gi.status === "created") {
      log.success("  ✓ .claude/.gitignore: chưa có → đã tạo (bảo vệ settings.json + lock).");
    } else if (gi.status === "failed") {
      log.warn(`  ! Cập nhật .claude/.gitignore: ${gi.warning}`);
    }
  } catch (err) {
    log.warn(`  ! Cập nhật .claude/.gitignore fail: ${err instanceof Error ? err.message : err}.`);
  }

  // Ticket #212 điểm (i)/2: seed hoặc merge `notes/orientation.md` từ template của pack.
  // `orientationStatus` giữ ở scope NGOÀI try vì điểm (ii) — append dòng `@notes/orientation.md`
  // vào CLAUDE.md — nằm cách đây ~110 dòng, phải chạy SAU mergeRootClaudeMdManaged (xem chú
  // thích tại điểm (ii)). Cùng khuôn với `patchResult` bên dưới. `undefined` (ném lỗi trước khi
  // gán) ⇒ điểm (ii) bỏ qua, sync không chết.
  let orientationStatus: OrientationStatus | undefined;
  try {
    const orientation = await ensureOrientationNoteFromPack(projectRoot);
    orientationStatus = orientation.status;
    if (orientation.status === "created") {
      log.success("  ✓ notes/orientation.md: chưa có → đã seed từ pack.");
    } else if (orientation.status === "replaced-block") {
      log.success("  ✓ notes/orientation.md: cập nhật block managed (giữ ghi chú của bạn).");
    } else if (orientation.status === "backed-up-replaced") {
      log.warn(
        `  ! notes/orientation.md (chưa có marker) → đã ghi bản mới. Bản cũ: ${orientation.backupPath ?? "(không tạo được backup)"}`,
      );
    } else if (orientation.status === "skipped-symlink" || orientation.status === "failed") {
      log.warn(`  ! notes/orientation.md: ${orientation.warning}`);
    }
  } catch (err) {
    log.warn(
      `  ! Cập nhật notes/orientation.md fail: ${err instanceof Error ? err.message : err}.`,
    );
  }

  // Merge settings + re-apply tools (giữ nguyên logic v2).
  log.info("Merge pack settings.json template vào project settings.json...");
  try {
    const mergeResult = await mergePackSettingsIntoProjectSettings(projectRoot);
    switch (mergeResult.action) {
      case "merged":
        log.success(`  ✓ settings.json merged (${mergeResult.changes.join("; ")})`);
        break;
      case "no-change":
        log.info("  - settings.json đã sync.");
        break;
      case "no-pack-template":
        log.dim("  - Pack không có templates/settings.json.tpl, skip.");
        break;
    }
  } catch (err) {
    log.warn(`  ! Merge settings.json fail: ${err instanceof Error ? err.message : err}.`);
  }

  try {
    await reapplyEnabledToolsAfterSync(projectRoot);
  } catch (err) {
    log.warn(`  ! Re-apply tools fail: ${err instanceof Error ? err.message : err}.`);
  }

  // Prune orphan tool hooks: pack đã BỎ 1 tool → xoá hook-entry mồ côi trỏ script
  // pack/tools/<T>/ không còn tồn tại. Chạy SAU reapply (live hook đã có mặt);
  // try/catch riêng (fail-soft, không abort sync — AC5). Flip state enabled=false
  // CHỈ khi prune hoàn tất (status pruned|no-op); DEFER khi settings-unreadable để
  // sync sau thử lại (B2); skipped-anomalous-pack có orphans rỗng → loop no-op (B1).
  try {
    const prune = await pruneOrphanToolHooks(projectRoot);
    const flipOk = prune.status === "pruned" || prune.status === "no-op";
    if (flipOk && prune.orphans.length > 0) {
      const state = await readToolState(projectRoot);
      for (const t of prune.orphans) {
        const prevVersion = state.tools[t]?.version ?? "unknown";
        await setToolState(projectRoot, t, { enabled: false, version: prevVersion });
      }
    }
    if (prune.changes.length > 0) {
      log.success(`  ✓ orphan hooks pruned (${prune.changes.join("; ")})`);
    } else if (flipOk && prune.orphans.length > 0) {
      log.info(`  - orphan tool(s) ${prune.orphans.join(", ")}: không có hook entry để xoá.`);
    }
    if (prune.warning) log.warn(`  ! Prune orphan hooks: ${prune.warning}`);
  } catch (err) {
    log.warn(`  ! Prune orphan hooks fail: ${err instanceof Error ? err.message : err}.`);
  }

  // Ticket #139: self-heal — gỡ env var chết `AVATAR_MODE: "{{mode}}"` (placeholder v3 không
  // bao giờ được thay, 0 code path đọc). Xoá theo GIÁ TRỊ nguyên văn, KHÔNG theo tên key:
  // value khác ⇒ user tự đặt ⇒ GIỮ NGUYÊN. Chạy SAU mọi writer settings.json khác để pack
  // (nếu tương lai lỡ ship lại key) không add ngược sau khi vừa dọn. try/catch riêng: sync
  // không bao giờ fail vì bước này. no-op thường → im lặng (không thêm nhiễu log).
  try {
    const modePrune = await pruneDeadAvatarModeEnvPlaceholder(projectRoot);
    if (modePrune.status === "pruned") {
      log.success(
        `  ✓ settings.json: ${modePrune.changes.join("; ")} (backup: ${modePrune.backupPath})`,
      );
    } else if (modePrune.keptForeignValue) {
      log.info("  - Giữ nguyên env AVATAR_MODE (giá trị do bạn tự đặt, không phải placeholder).");
    } else if (modePrune.warning) {
      log.warn(`  ! Bỏ qua dọn AVATAR_MODE: ${modePrune.warning}`);
    }
  } catch (err) {
    log.warn(`  ! Dọn env AVATAR_MODE fail: ${err instanceof Error ? err.message : err}.`);
  }

  // Cập nhật managed block trong CLAUDE.md (Workflows&Rules, hướng dẫn pack...)
  // mà giữ context user. Fail-open: lỗi → cảnh báo, không chặn sync.
  log.info("Cập nhật managed block trong CLAUDE.md...");
  try {
    const meta = await readClaudeMdMeta(projectRoot, join(projectRoot, "CLAUDE.md"));
    const vars = {
      projectName: meta.projectName,
      projectDescription: meta.projectDescription,
      teamOwner: meta.teamOwner,
      avatarVersion: readCliVersion(),
      packVersion: installedVersion,
      lastScan: new Date().toISOString(),
    };
    const r = await mergeRootClaudeMdManaged(projectRoot, vars);
    switch (r.outcome) {
      case "replaced-block":
        log.success("  ✓ CLAUDE.md managed block đã cập nhật (giữ context user ngoài block).");
        break;
      case "replaced-whole":
        log.success("  ✓ CLAUDE.md (chưa có marker) → thay hoàn toàn bằng bản Avatar mới.");
        break;
      case "skipped-no-file":
        log.dim("  - Chưa có CLAUDE.md (chạy 'avatar init' để tạo), skip.");
        break;
    }
  } catch (err) {
    log.warn(`  ! Cập nhật CLAUDE.md fail: ${err instanceof Error ? err.message : err}.`);
  }

  // Ticket #212 điểm (ii)/2: append `@notes/orientation.md` vào vùng user của CLAUDE.md.
  // ⚠️ VỊ TRÍ CÓ CHỦ ĐÍCH — phải đứng SAU khối mergeRootClaudeMdManaged ngay trên: CLAUDE.md
  // KHÔNG có marker rơi nhánh "replaced-whole" = thay hoàn toàn file
  // (`merge-claude-md-managed-block.ts:84-87`) ⇒ đặt trước thì dòng vừa append bị xoá ngay
  // trong CÙNG lần sync. ĐỪNG gộp lên điểm (i).
  if (orientationStatus) {
    try {
      const imported = await appendOrientationImportToClaudeMd(projectRoot, orientationStatus);
      if (imported.appended) {
        log.success("  ✓ CLAUDE.md: thêm '@notes/orientation.md' (bản đồ workspace auto-load).");
      } else if (imported.warning) {
        log.warn(`  ! CLAUDE.md: ${imported.warning}`);
      }
    } catch (err) {
      log.warn(
        `  ! Thêm '@notes/orientation.md' vào CLAUDE.md fail: ${err instanceof Error ? err.message : err}.`,
      );
    }
  }

  // Ticket #138 phase-02: đích ghi Avatar block đổi từ .gitignore (tracked —
  // gây 4 repo trong src/ dirty vĩnh viễn) sang <git-common-dir>/info/exclude
  // (untracked, git-native). Idempotent (marker), fail-soft 2 tầng.
  // `patchResult` giữ ở scope ngoài try — phase-03 nối bước strip .gitignore
  // sau đoạn này, cần `patchResult.patched` làm `onlyRepos` (bất biến B1: chỉ
  // gỡ rào cũ khỏi repo đã dựng xong rào mới).
  log.info("Ghi Avatar block vào <gitdir>/info/exclude cho repos trong src/...");
  let patchResult: PatchExcludeResult | undefined;
  try {
    patchResult = await patchAllReposGitExclude(projectRoot);
    if (patchResult.gitOutcome === "gitUnavailable") {
      log.warn(
        "  ! Không chạy được 'git' trên PATH — KHÔNG ghi được info/exclude cho repo nào, và ĐÃ BỎ bước gỡ block khỏi .gitignore (giữ nguyên ignore hiện có).",
      );
    } else if (patchResult.gitOutcome === "gitTooOld") {
      // In CON SỐ version đo được: "quá cũ" trần buộc user tự chạy
      // `git --version` mới biết máy mình cách sàn bao xa. `?? "?"` cho ca
      // probe không parse được version (gitVersion optional).
      log.warn(
        `  ! git ${patchResult.gitVersion ?? "?"} quá cũ (cần >= 2.31 cho --path-format) — bỏ qua ghi exclude + bỏ bước gỡ .gitignore.`,
      );
    } else {
      if (patchResult.patched.length)
        log.success(`  ✓ Avatar block info/exclude → ${patchResult.patched.length} repo`);
      if (patchResult.missing.length)
        log.dim(`  - Manifest có repo thiếu folder: ${patchResult.missing.join(", ")}`);
      if (patchResult.skippedNotGitRepo.length)
        log.info(`  - Bỏ qua (không phải git repo): ${patchResult.skippedNotGitRepo.join(", ")}`);
      if (patchResult.corrupted.length)
        log.warn(
          `  ⚠ info/exclude hỏng marker (START không có END) ở: ${patchResult.corrupted.join(", ")} — sửa tay, Avatar KHÔNG tự đụng.`,
        );
      if (patchResult.failed.length)
        log.warn(`  ! Ghi info/exclude lỗi ở: ${patchResult.failed.map((f) => f.name).join(", ")}`);
    }
  } catch (err) {
    log.warn(
      `  ! Ghi Avatar block info/exclude fail: ${err instanceof Error ? err.message : err}.`,
    );
  }

  // Ticket #138 phase-03: self-heal — gỡ block Avatar CŨ sót lại trong
  // .gitignore của repo (đích ghi mới là info/exclude, phase-02). B5 — gác
  // TRƯỚC khi gọi: `patchResult` undefined (bước trên throw) HOẶC gitOutcome
  // ≠ "ok" → BỎ HẲN bước này (không gọi với mảng rỗng — người đọc log phải
  // phân biệt "không có gì để làm" với "đã bỏ vì bước trước hỏng"). B1 — chỉ
  // gỡ ở repo nằm trong `patchResult.patched` (đã dựng xong rào mới); gỡ rào
  // cũ trước khi rào mới sẵn sàng = mất ignore VĨNH VIỄN.
  if (patchResult && patchResult.gitOutcome === "ok") {
    try {
      const strip = await stripAvatarBlockFromReposGitignore(projectRoot, {
        onlyRepos: patchResult.patched,
      });
      const hasStripOutput =
        strip.stripped.length > 0 ||
        strip.corrupted.length > 0 ||
        strip.skippedExcludeNotWritten.length > 0 ||
        strip.failed.length > 0;
      if (hasStripOutput) {
        log.info("Gỡ Avatar block cũ khỏi .gitignore repo (đã chuyển sang info/exclude)...");
        if (strip.stripped.length > 0) {
          const backupDir = dirname(strip.backups[0]?.backupPath ?? "");
          log.success(
            `  ✓ Đã gỡ Avatar block khỏi .gitignore ở ${strip.stripped.length} repo: ${strip.stripped.join(", ")} (backup: ${backupDir})`,
          );
          // D5 — hướng dẫn 2 vế bắt buộc, KHÔNG rút gọn. Avatar KHÔNG tự commit.
          log.info(
            "    → Nếu block CHƯA từng được commit: git status của repo sẽ SẠCH ngay, không cần làm gì.",
          );
          log.info(
            "    → Nếu block ĐÃ từng được commit (máy member khác): repo sẽ hiện 'M .gitignore' (hoặc 'D .gitignore'). Đây là thay đổi ĐÚNG, do Avatar chuyển rule sang <gitdir>/info/exclude. Hãy COMMIT nó như thay đổi bình thường — ĐỪNG 'git checkout -- .gitignore' để \"dọn\", làm vậy block quay lại và vòng lặp dirty tái diễn mỗi lần sync.",
          );
        }
        // D3 — bắt buộc log riêng khi XOÁ FILE (im lặng xoá file người dùng là
        // không chấp nhận được).
        for (const name of strip.removedGitignore) {
          const b = strip.backups.find((x) => x.name === name);
          log.success(
            `  ✓ Đã XOÁ .gitignore (rỗng sau khi gỡ block) ở: ${name} — backup: ${b?.backupPath ?? "?"}`,
          );
        }
        if (strip.corrupted.length > 0) {
          log.warn(
            `  ⚠ .gitignore hỏng marker (START không có END) ở: ${strip.corrupted.join(", ")} — sửa tay, Avatar KHÔNG tự đụng.`,
          );
        }
        if (strip.skippedExcludeNotWritten.length > 0) {
          log.warn(
            `  ⚠ Giữ nguyên .gitignore ở: ${strip.skippedExcludeNotWritten.join(", ")} — chưa ghi được info/exclude cho repo này (gỡ bây giờ là mất ignore hoàn toàn).`,
          );
        }
        if (strip.failed.length > 0) {
          log.warn(`  ! Gỡ block lỗi ở: ${strip.failed.map((f) => f.name).join(", ")}`);
        }
      }
    } catch (err) {
      log.warn(`  ! Gỡ block .gitignore cũ fail: ${err instanceof Error ? err.message : err}.`);
    }
  } else {
    log.warn("  - Bỏ bước gỡ block khỏi .gitignore (chưa ghi được info/exclude).");
  }

  // Ticket #129: self-heal — gỡ pre-push hook cũ (dead code v2, cài trước khi
  // installer bị gỡ ở phase-01) cho mọi repo trong src/ CÓ marker Avatar. Hook lạ
  // của member (không marker) giữ nguyên (skippedForeign). Backup trước khi xoá
  // nằm TRONG pruner (bất biến — xem phase-02 §Requirements). try/catch riêng:
  // sync không bao giờ fail vì bước này.
  try {
    const prune = await pruneStaleAvatarPrePushHooks(projectRoot);
    const hasOutput =
      prune.pruned.length > 0 ||
      prune.skippedForeign.length > 0 ||
      prune.skippedNonDirGit.length > 0 ||
      prune.missing.length > 0 ||
      prune.failed.length > 0;
    if (hasOutput) {
      log.info("Dọn pre-push hook Avatar cũ (dead code v2) trong src/...");
      if (prune.pruned.length > 0) {
        const backupDir = dirname(prune.backups[0]?.backupPath ?? "");
        log.success(
          `  ✓ Đã gỡ pre-push hook ở ${prune.pruned.length} repo: ${prune.pruned.join(", ")}  (backup: ${backupDir})`,
        );
      }
      if (prune.skippedForeign.length > 0) {
        log.info(
          `  - Giữ nguyên pre-push KHÔNG phải của Avatar ở: ${prune.skippedForeign.join(", ")}`,
        );
      }
      if (prune.skippedNonDirGit.length > 0) {
        log.dim(
          `  - Bỏ qua (.git là file — worktree/submodule): ${prune.skippedNonDirGit.join(", ")}`,
        );
      }
      if (prune.missing.length > 0) {
        log.dim(`  - Repo trong manifest nhưng thiếu thư mục: ${prune.missing.join(", ")}`);
      }
      if (prune.failed.length > 0) {
        log.warn(`  ! Gỡ hook lỗi ở: ${prune.failed.map((f) => f.name).join(", ")}`);
      }
    }
  } catch (err) {
    log.warn(`  ! Dọn pre-push hook cũ fail: ${err instanceof Error ? err.message : err}.`);
  }

  // GitNexus = hạ tầng cốt lõi → mỗi sync đảm bảo đã cài + bản mới nhất.
  // Fail-soft: lỗi npm/mạng → warn, không chặn sync.
  try {
    await ensureGitnexusInstalledAndLatest();
  } catch (err) {
    log.warn(`  ! GitNexus check fail: ${err instanceof Error ? err.message : err}.`);
  }

  // Security scanner (gitleaks secret + trivy CVE) cho pull-request-checker.
  // Cài cái thiếu qua brew, fail-soft (không brew / lỗi → gate tự skip).
  try {
    ensureSecurityScannersInstalled();
  } catch (err) {
    log.warn(`  ! Scanner check fail: ${err instanceof Error ? err.message : err}.`);
  }

  // #211: ghi workspace vào registry `~/.avatar/workspaces.json` để `avatar sync --all`
  // (phase-02) biết máy này có những workspace nào. Đặt ở CUỐI, sau khi sync đã chạy
  // trọn: các nhánh `--dry-run` return sớm (:173 :182 :198) và nhánh lỗi `process.exit(1)`
  // KHÔNG đi qua đây — cố ý, `avatar sync --dry-run` không phải đường đăng ký.
  // Fail-soft: registry là tiện ích, không được làm hỏng một lần sync đã thành công.
  try {
    await recordWorkspaceInRegistry(projectRoot);
  } catch (err) {
    log.dim(`  (registry workspace bỏ qua: ${err instanceof Error ? err.message : err})`);
  }

  log.success(`Synced team-ai-pack → ${installedVersion}.`);
}

function reportResults(results: SyncMountedDirResult[], force: boolean): void {
  for (const r of results) {
    switch (r.action) {
      case "created":
        log.info(`  ✓ ${r.dir} → symlinked (new)`);
        break;
      case "updated":
        log.info(`  ✓ ${r.dir} → symlink refreshed`);
        break;
      case "backed-up-and-linked":
        log.info(`  ✓ ${r.dir} → symlinked (backup: ${r.backupPath})`);
        break;
      case "source-missing":
        log.warn(`  - ${r.dir} → pack không có dir này, skip`);
        break;
      case "skipped-conflict":
        log.warn(`  ! ${r.dir} → CONFLICT: existing real dir. Dùng --force để backup + override.`);
        break;
    }
  }
  const conflicts = results.filter((r) => r.action === "skipped-conflict").length;
  if (conflicts > 0 && !force) {
    log.warn(`${conflicts} mount dir(s) skip do conflict. Chạy lại với --force.`);
  }
}

/** `--force-open-sessions` lặp được: mỗi lần gõ thêm MỘT path, không ghi đè lần trước. */
function collectRepeatable(value: string, previous: string[]): string[] {
  return [...previous, value];
}

export function registerSyncCommand(program: Command): void {
  program
    .command("sync")
    .description("Tải team-ai-pack mới nhất (tarball Supabase) + tạo symlink farm")
    .option("--force", "Override .claude/<dir>/ nếu là real dir (backup trước)")
    .option("--pack-version <tag>", "Pin version cụ thể (vd: v0.2.0 hoặc v0.2.0-beta.0)")
    .option("--dry-run", "Hiển thị preview, không apply")
    .option("--json", "Output JSON cho máy đọc (chỉ dùng cùng --dry-run)")
    .option("--local <path>", "Cài pack từ thư mục local thay vì tải Supabase (sandbox/dev)")
    .option("--all", "Sync MỌI workspace đã đăng ký trên máy (không cần cd vào từng nơi)")
    .option("--include-downgrades", "Với --all: sync cả workspace đang ở bản MỚI HƠN đích")
    .option(
      "--stale-days <days>",
      `Với --all: bỏ qua workspace không hoạt động quá N ngày (mặc định ${DEFAULT_STALE_DAYS})`,
    )
    .option(
      "--force-open-sessions <path>",
      "Với --all: sync workspace này dù đang có phiên Claude Code (lặp được cho nhiều path)",
      collectRepeatable,
      [],
    )
    .action(async (opts: SyncOptions & SyncAllOptions & { all?: boolean }) => {
      // ⚠️ Rẽ TRƯỚC `syncAction`: dòng đầu của nó là `assertInAvatarWorkspaceOrExit(cwd)`, chạy
      // trước mọi nhánh cờ ⇒ đi qua đó thì `--all` gõ ở `~` sẽ exit trước khi chạm registry.
      // Đường sync 1-workspace bên dưới KHÔNG đổi một dòng nào (AC12).
      if (opts.all === true) return await runSyncAllAcrossRegisteredWorkspaces(opts);
      return await syncAction(opts);
    });
}
